import { inject, Injectable, Signal } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';

import { OperatorFunction, catchError, Observable, share } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppError, SpinnerService } from '@hglobal/common';
import { environment } from '@pt/environment';
import { TextFormaterHelper } from '@pt/helpers';

import { ToastI18nService } from './toast-i18n.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { SentryErrorHandler } from './sentry-error-handler.service';

@Injectable({ providedIn: 'root' })
export class NativeStoreHelperService {
  readonly #toastService = inject(ToastI18nService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #translateService = inject(TranslateService);
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);
  readonly #sentryErrorHandler = inject(SentryErrorHandler);

  prepareReq<T>(observable$: Observable<T>): Observable<T> {
    return this.#spinnerService.observe(observable$.pipe(this.catchError(), share()), true);
  }

  isLoading(): Signal<boolean> {
    return toSignal(this.#spinnerService.isSpinning$);
  }

  catchError<T>(): OperatorFunction<T, T> {
    return catchError((error: AppError) => {
      this.handleServerError(error);
      throw error;
    });
  }

  handleServerError(error: AppError): void {
    const excludedStatuses = [HttpStatusCode.NotFound, HttpStatusCode.Unauthorized];
    if (excludedStatuses.includes(error?.originalError?.status)) {
      return;
    }

    if (!environment.production) {
      console.error('Server error: ', error);
    }

    this.#googleAnalyticsService.pushCustomEvent('error', 'toast', {
      status: TextFormaterHelper.transformToSnakeCase(
        `error_${error.originalError.status}.${error.code}`,
      ),
    });

    this.#sentryErrorHandler.sendErrorToSentry(error);
    this.#toastService.showError(this.#getErrorMessage(error));
  }

  #getErrorMessage(error: AppError): string {
    if (error.code) {
      const code = `errors.${error.code}`;
      const message = this.#translateService.instant(code);
      return message === code ? error.message || code : message;
    } else {
      return error.message || this.#translateService.instant('errors.unknown');
    }
  }
}
